export const defaultUnits: Record<string, string> = {
  wbc: 'E9/L',
  rbc: 'E12/L',
  hemoglobin: 'g/L',
  hematocrit: 'L/L',
  mcv: 'fL',
  mch: 'pg',
  mchc: 'g/L',
  rdw: '%',
  mpv: 'fL',
  platelet_count: 'E9/L',
  neutrophils: 'E9/L',
  lymphocytes: 'E9/L',
  monocytes: 'E9/L',
  eosinophils: 'E9/L',
  basophils: 'E9/L',
  immature_granulocytes: 'E9/L',
  nrbc: 'E9/L',
  glucose_fasting: 'mmol/L',
  glucose_random: 'mmol/L',
  insulin_fasting: 'uIU/mL',
  HbA1C: '%',
  creatinine: 'μmol/L',
  egfr: 'mL/min/1.73m²',
  albumin: 'g/L',
  alkaline_phosphatase: 'U/L',
  alanine_transaminase: 'U/L',
  ast: 'U/L',
  lipase: 'U/L',
  ggt: 'U/L',
  phosphorus: 'mmol/L',
  sodium: 'mmol/L',
  potassium: 'mmol/L',
  chloride: 'mmol/L',
  calcium: 'mmol/L',
  magnesium: 'mmol/L',
  uric_acid: 'μmol/L',
  cholesterol: 'mmol/L',
  triglyceride: 'mmol/L',
  lipoprotein_a: 'nmol/L',
  homocysteine: 'µmol/L',
  hdl_cholesterol: 'mmol/L',
  ldl_cholesterol: 'mmol/L',
  non_hdl_cholesterol: 'mmol/L',
  ferritin: 'µg/L',
  vitamin_b12: 'pmol/L',
  tsh: 'mIU/L',
  free_t3: 'pmol/L',
  free_t4: 'pmol/L',
  prolactin: 'mIU/L',
  lh: 'IU/L',
  fsh: 'IU/L',
  estradiol: 'pmol/L',
  total_testosterone: 'nmol/L',
  free_testosterone: 'pmol/L',
  bioavailable_testosterone: 'nmol/L',
  shbg: 'nmol/L',
  dht: 'nmol/L',
  cortisol: 'nmol/L',
  dhea_s: 'µmol/L',
  progesterone: 'nmol/L',
  '25-hydroxyvitamin_d': 'nmol/L',
  iron: 'µmol/L',
  transferrin: 'g/L',
  tibc: 'µmol/L',
  transferrin_saturation: '%',
  hours_fasting: 'hours',
  crp: 'mg/L',
  total_psa: 'µg/L',
  free_psa: 'µg/L'
};

// Constants for phenotypic age calculation
export const PHENOTYPIC_AGE_CONSTANTS = {
  // Linear predictor coefficients
  LINEAR_PREDICTOR: {
    INTERCEPT: -19.9067,
    COEFFICIENTS: {
      albumin: -0.0336,
      creatinine: 0.0095,
      glucose: 0.1953,
      crp: 0.0954,
      lymph_pct: -0.012,
      mcv: 0.0268,
      rdw: 0.3306,
      alp: 0.0019,
      wbc: 0.0554,
      age: 0.0804
    }
  },
  CRP: {
    VALUE: 1.2,
    CONVERSION_RATE: 0.1
  },

  // Gompertz model constants
  GOMPERTZ: {
    GAMMA: 0.0076927,
    TIME_HORIZON: 120 // 10 years in months
  },

  // Phenotypic age conversion constants
  CONVERSION: {
    ALPHA: 141.50225,
    BETA: 0.09165,
    KAPPA: -0.00553
  }
};

// Type definition for lab test configuration
export interface LabTestConfig {
  name: string;
  required: boolean;
  alternatives?: string[];
  extractAs?: string;
}

export const REQUIRED_LAB_TESTS: LabTestConfig[] = [
  { name: 'albumin', required: true },
  { name: 'creatinine', required: true },
  {
    name: 'glucose_group',
    required: true,
    alternatives: ['glucose_fasting', 'glucose_random'],
    extractAs: 'glucose'
  },
  { name: 'lymphocytes', required: true },
  { name: 'wbc', required: true },
  { name: 'mcv', required: true },
  { name: 'rdw', required: true },
  { name: 'alkaline_phosphatase', required: true },
  { name: 'crp', required: false }
];

export const HOMA_IR_REQUIRED_LAB_TESTS: LabTestConfig[] = [
  { name: 'glucose_fasting', required: true },
  { name: 'insulin_fasting', required: true }
];

export const TYG_REQUIRED_LAB_TESTS: LabTestConfig[] = [
  { name: 'glucose_fasting', required: true },
  { name: 'triglyceride', required: true }
];

/** Merge lab test configs by `name` (union alternatives, OR `required`). */
function mergeLabTestConfigs(sources: LabTestConfig[][]): LabTestConfig[] {
  const map = new Map<string, LabTestConfig>();
  for (const arr of sources) {
    for (const test of arr) {
      const prev = map.get(test.name);
      if (!prev) {
        map.set(test.name, { ...test });
        continue;
      }
      const merged: LabTestConfig = {
        ...prev,
        required: prev.required || test.required,
        extractAs: prev.extractAs ?? test.extractAs
      };
      const alts = new Set([
        ...(prev.alternatives || []),
        ...(test.alternatives || [])
      ]);
      if (alts.size) {
        merged.alternatives = [...alts];
      }
      map.set(test.name, merged);
    }
  }
  return [...map.values()];
}

/**
 * Required tests for drawer alerts: phenotypic age inputs plus HOMA-IR and TyG prerequisites.
 * `REQUIRED_LAB_TESTS` stays separate for phenotypic-age extraction only.
 */
export const REQUIRED_FOR_ANALYSES_LAB_TESTS: LabTestConfig[] =
  mergeLabTestConfigs([
    REQUIRED_LAB_TESTS,
    HOMA_IR_REQUIRED_LAB_TESTS,
    TYG_REQUIRED_LAB_TESTS
  ]);

/**
 * Shared Jack/Jill panel markers (canonical `LabTestResult.testName` keys).
 * Excludes calculated-only rows (eGFR, Chol/HDL ratio, HOMA-IR, TyG) — those are not raw analyte extractions.
 */
const SHARED_PANEL_BIOMARKER_KEYS = [
  'glucose_fasting',
  'non_hdl_cholesterol',
  'HbA1C',
  'ldl_cholesterol',
  'insulin_fasting',
  'triglyceride',
  'sodium',
  'cholesterol',
  'potassium',
  'hdl_cholesterol',
  'creatinine',
  'lipase',
  'albumin',
  'alkaline_phosphatase',
  'alanine_transaminase',
  'ggt',
  'calcium',
  'phosphorus',
  'crp',
  'monocytes',
  'wbc',
  'eosinophils',
  'neutrophils',
  'basophils',
  'immature_granulocytes',
  'ferritin',
  'lymphocytes',
  'iron',
  'tibc',
  'total_testosterone',
  'estradiol',
  'prolactin',
  'tsh',
  'fsh',
  'lh',
  'free_t3',
  'free_t4',
  'hemoglobin',
  'hematocrit',
  'mcv',
  'mch',
  'mchc',
  'rdw',
  'platelet_count',
  'vitamin_b12',
  'rbc',
  'nrbc'
] as const;

const JACK_ONLY_BIOMARKER_KEYS = [
  'uric_acid',
  'magnesium',
  'ast',
  'lipoprotein_a',
  'homocysteine',
  'free_testosterone',
  'shbg',
  'cortisol',
  'total_psa'
] as const;

const JILL_ONLY_BIOMARKER_KEYS = ['dhea_s', 'progesterone'] as const;

const CBC_BIOMARKER_KEYS = [
  'wbc',
  'rbc',
  'hemoglobin',
  'hematocrit',
  'mcv',
  'mch',
  'mchc',
  'rdw',
  'platelet_count',
  'neutrophils',
  'lymphocytes',
  'monocytes',
  'eosinophils',
  'basophils',
  'immature_granulocytes',
  'nrbc'
] as const;

const LIPID_BIOMARKER_KEYS = [
  'cholesterol',
  'hdl_cholesterol',
  'ldl_cholesterol',
  'triglyceride',
  'non_hdl_cholesterol'
] as const;

const BASE_BIOCHEM_BIOMARKER_KEYS = [
  'glucose_fasting',
  'HbA1C',
  'creatinine',
  'sodium',
  'potassium',
  'alanine_transaminase',
  'albumin',
  'alkaline_phosphatase'
] as const;

const TRT_OTHER_BIOMARKER_KEYS = [
  'free_testosterone',
  'total_testosterone',
  'vitamin_b12',
  'estradiol',
  'ferritin',
  'lh',
  'fsh',
  'tsh',
  'prolactin',
  'lipase',
  'crp',
  'insulin_fasting'
] as const;

const WL_OTHER_BIOMARKER_KEYS = [
  'vitamin_b12',
  'iron',
  'tibc',
  'transferrin_saturation',
  'ferritin',
  'tsh',
  'lipase',
  'crp',
  'insulin_fasting'
] as const;

/** TRT lab requisition panel (e.g. trt1on / variant trt). */
export const EXPECTED_BIOMARKER_KEYS_TRT: readonly string[] = [
  ...BASE_BIOCHEM_BIOMARKER_KEYS,
  ...LIPID_BIOMARKER_KEYS,
  ...CBC_BIOMARKER_KEYS,
  'total_psa',
  ...TRT_OTHER_BIOMARKER_KEYS
];

/** WL lab requisition panel (e.g. wlon / variant wl). */
export const EXPECTED_BIOMARKER_KEYS_WL: readonly string[] = [
  ...BASE_BIOCHEM_BIOMARKER_KEYS,
  ...LIPID_BIOMARKER_KEYS,
  ...CBC_BIOMARKER_KEYS,
  ...WL_OTHER_BIOMARKER_KEYS
];

/** Full Jack (male) panel = shared + male-only markers. */
export const EXPECTED_BIOMARKER_KEYS_JACK: readonly string[] = [
  ...SHARED_PANEL_BIOMARKER_KEYS,
  ...JACK_ONLY_BIOMARKER_KEYS
];

/** Full Jill (female) panel = shared + female-only markers. */
export const EXPECTED_BIOMARKER_KEYS_JILL: readonly string[] = [
  ...SHARED_PANEL_BIOMARKER_KEYS,
  ...JILL_ONLY_BIOMARKER_KEYS
];

export type LabReqPanelType = 'wl' | 'trt' | 'lgv';

export interface LabReqPanelSource {
  templateId?: string | null;
  templateVariant?: string | null;
  createdAt?: Date | string | null;
}

const V1_TEMPLATE_PANEL_TYPE: Record<string, LabReqPanelType> = {
  wlon: 'wl',
  trt1on: 'trt',
  lgvon: 'lgv'
};

/** Map lab requisition template to WL / TRT / LGV panel type. */
export function resolveLabReqPanelType(
  templateId?: string | null,
  templateVariant?: string | null
): LabReqPanelType | undefined {
  const id = templateId?.trim?.().toLowerCase?.() || '';
  if (id in V1_TEMPLATE_PANEL_TYPE) {
    return V1_TEMPLATE_PANEL_TYPE[id];
  }
  const variant = templateVariant?.trim?.().toLowerCase?.();
  if (variant === 'wl' || variant === 'trt' || variant === 'lgv') {
    return variant;
  }
  return undefined;
}

/** UTC calendar day (ms) for date-only collectedAt / createdAt comparisons. */
function utcCalendarDayMs(value: Date | string): number | undefined {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return undefined;
  }
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** End of UTC calendar day — includes all reqs created on the collection date. */
export function endOfCollectedAtCalendarDay(
  collectedAt: Date | string | null | undefined
): Date | undefined {
  const dayMs = collectedAt == null ? undefined : utcCalendarDayMs(collectedAt);
  if (dayMs == null) {
    return undefined;
  }
  return new Date(dayMs + 24 * 60 * 60 * 1000 - 1);
}

/** Last requisition created on or before the lab result collection date. */
export function findLastLabReqBeforeCollectedAt<T extends LabReqPanelSource>(
  labReqs: readonly T[] | null | undefined,
  collectedAt: Date | string | null | undefined
): T | undefined {
  if (!labReqs?.length || collectedAt == null) {
    return undefined;
  }
  const collectedDayMs = utcCalendarDayMs(collectedAt);
  if (collectedDayMs == null) {
    return undefined;
  }
  let best: T | undefined;
  let bestMs = -Infinity;
  for (const req of labReqs) {
    if (!req.createdAt) {
      continue;
    }
    const createdDayMs = utcCalendarDayMs(req.createdAt);
    if (createdDayMs == null || createdDayMs > collectedDayMs) {
      continue;
    }
    const createdMs = new Date(req.createdAt).getTime();
    if (createdMs > bestMs) {
      best = req;
      bestMs = createdMs;
    }
  }
  return best;
}

/** How many panel biomarkers are present (matched by `testName`, case-insensitive). */
export function countExpectedPanelKeysPresent(
  results: Array<{ testName?: string }> | null | undefined,
  expectedKeys: readonly string[]
): number {
  const names = new Set(
    (results ?? [])
      .map(r => r.testName?.trim().toLowerCase())
      .filter((x): x is string => !!x)
  );
  let n = 0;
  for (const key of expectedKeys) {
    if (names.has(key.toLowerCase())) {
      n++;
    }
  }
  return n;
}

/** Expected panel keys with no matching `testName` in `results` (same matching rules as count). */
export function getMissingExpectedPanelKeys(
  results: Array<{ testName?: string }> | null | undefined,
  expectedKeys: readonly string[]
): string[] {
  const names = new Set(
    (results ?? [])
      .map(r => r.testName?.trim().toLowerCase())
      .filter((x): x is string => !!x)
  );
  return expectedKeys.filter(k => !names.has(k.toLowerCase()));
}

/** Display label for a canonical biomarker key (drawer / tooltips). */
export function formatBiomarkerKeyLabel(key: string): string {
  if (key === 'HbA1C') {
    return 'HbA1c';
  }
  return key
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, m => m.toUpperCase());
}

/** Derived counts from panel key lists (Jack = male, Jill = female). */
export const EXPECTED_BIOMARKER_COUNT = {
  male: EXPECTED_BIOMARKER_KEYS_JACK.length,
  female: EXPECTED_BIOMARKER_KEYS_JILL.length,
  other: EXPECTED_BIOMARKER_KEYS_JACK.length
} as const;

export function getExpectedBiomarkerKeysForSex(
  sex: keyof typeof EXPECTED_BIOMARKER_COUNT
): readonly string[] {
  return sex === 'female' ? EXPECTED_BIOMARKER_KEYS_JILL : EXPECTED_BIOMARKER_KEYS_JACK;
}

export function getExpectedBiomarkerKeysForPanel(
  panelType: LabReqPanelType,
  sex: keyof typeof EXPECTED_BIOMARKER_COUNT
): readonly string[] {
  switch (panelType) {
    case 'wl':
      return EXPECTED_BIOMARKER_KEYS_WL;
    case 'trt':
      return EXPECTED_BIOMARKER_KEYS_TRT;
    case 'lgv':
    default:
      return getExpectedBiomarkerKeysForSex(sex);
  }
}

export function formatLabReqPanelLabel(
  panelType: LabReqPanelType,
  sex: keyof typeof EXPECTED_BIOMARKER_COUNT
): string {
  if (panelType === 'lgv') {
    return sex === 'female'
      ? 'LGV female panel'
      : sex === 'male'
      ? 'LGV male panel'
      : 'LGV panel';
  }
  return `${panelType.toUpperCase()} panel`;
}

export function resolveExpectedBiomarkerKeysForLabResult(
  lab: {
    meta?: { patientSex?: 'male' | 'female' | 'other' };
    organization?: string;
    collectedAt?: Date | string | null;
  },
  options?: {
    customerGender?: 'male' | 'female' | 'other' | null | undefined;
    labReqs?: readonly LabReqPanelSource[] | null;
    labReq?: LabReqPanelSource | null;
    fallbackPanelType?: LabReqPanelType;
  }
): {
  keys: readonly string[];
  panelType: LabReqPanelType;
  labReq?: LabReqPanelSource;
  panelLabel: string;
} {
  const sex = resolveExpectedBiomarkerSexForLabResult(lab, options?.customerGender);
  const labReq =
    options?.labReq ??
    findLastLabReqBeforeCollectedAt(options?.labReqs, lab.collectedAt);
  const panelType =
    (labReq
      ? resolveLabReqPanelType(labReq.templateId, labReq.templateVariant)
      : undefined) ??
    options?.fallbackPanelType ??
    'lgv';
  return {
    keys: getExpectedBiomarkerKeysForPanel(panelType, sex),
    panelType,
    labReq,
    panelLabel: formatLabReqPanelLabel(panelType, sex)
  };
}

export interface LabResultPanelStats {
  keys: readonly string[];
  panelType: LabReqPanelType;
  labReq?: LabReqPanelSource;
  present: number;
  expected: number;
  missing: string[];
  missingLabels: string[];
  panelLabel: string;
}

/** Resolve expected panel + present/missing counts for a lab result. */
export function getLabResultPanelStats(
  lab: {
    meta?: { patientSex?: 'male' | 'female' | 'other' };
    organization?: string;
    collectedAt?: Date | string | null;
    results?: Array<{ testName?: string }> | null;
  },
  options?: {
    customerGender?: 'male' | 'female' | 'other' | null | undefined;
    labReqs?: readonly LabReqPanelSource[] | null;
    labReq?: LabReqPanelSource | null;
    fallbackPanelType?: LabReqPanelType;
  }
): LabResultPanelStats {
  const { keys, panelType, labReq, panelLabel } =
    resolveExpectedBiomarkerKeysForLabResult(lab, options);
  const missing = getMissingExpectedPanelKeys(lab.results, keys);
  return {
    keys,
    panelType,
    labReq,
    present: countExpectedPanelKeysPresent(lab.results, keys),
    expected: keys.length,
    missing,
    missingLabels: missing.map(formatBiomarkerKeyLabel),
    panelLabel
  };
}

/** Tooltip suffix for panel completeness (e.g. "Based on TRT lab req."). */
export function formatPanelReqTooltip(panelLabel: string): string {
  return ` Based on ${panelLabel.replace(/ panel$/, '')} lab req.`;
}

/** Tooltip for panel completeness column / tag hover. */
export function formatPanelCompletenessTooltip(
  stats: Pick<LabResultPanelStats, 'missingLabels' | 'panelLabel'>
): string {
  const reqHint = formatPanelReqTooltip(stats.panelLabel);
  if (!stats.missingLabels.length) {
    return `Panel complete: all expected biomarkers are present in extracted rows.${reqHint}`;
  }
  return `Missing ${stats.missingLabels.length}: ${stats.missingLabels.join(', ')}.${reqHint}`;
}

/**
 * Sex used for Jack/Jill expected biomarker keys — same order as EMR:
 * customer profile → persisted extraction sex → org brand → other.
 */
export function resolveExpectedBiomarkerSexForLabResult(
  lab: {
    meta?: { patientSex?: 'male' | 'female' | 'other' };
    organization?: string;
  },
  customerGender?: 'male' | 'female' | 'other' | null | undefined
): keyof typeof EXPECTED_BIOMARKER_COUNT {
  if (
    customerGender === 'male' ||
    customerGender === 'female' ||
    customerGender === 'other'
  ) {
    return customerGender;
  }
  const fromMeta = lab.meta?.patientSex;
  if (fromMeta === 'male' || fromMeta === 'female' || fromMeta === 'other') {
    return fromMeta;
  }
  if (lab.organization === 'jill-health') {
    return 'female';
  }
  if (lab.organization === 'jack-health') {
    return 'male';
  }
  return 'other';
}

/** Normalize AI / PDF sex strings for `meta.patientSex`. */
export function normalizePatientSexFromExtraction(
  sex: string | null | undefined
): keyof typeof EXPECTED_BIOMARKER_COUNT | undefined {
  if (sex == null) {
    return undefined;
  }
  const s = String(sex).trim().toLowerCase();
  if (!s) {
    return undefined;
  }
  if (s === 'm' || s === 'male') {
    return 'male';
  }
  if (s === 'f' || s === 'female') {
    return 'female';
  }
  return 'other';
}

export const LAB_RESULT_UNIT_CONVERSIONS = {
  GLUCOSE_TO_MMOL_L: {
    MG_DL_FACTOR: 0.0555
  },
  TRIGLYCERIDE_TO_MMOL_L: {
    MG_DL_FACTOR: 0.0113
  },
  INSULIN_TO_UIU_ML: {
    PMOL_L_FACTOR: 0.144
  }
};
